/**
 * DISC-IMPR-B + C + Discovery Phase 2 — ScanTriggerModal
 *
 * Pre-scan picker that lets the operator:
 *   • Choose which engines to run (checkboxes).
 *   • See per-engine targets (DISC-IMPR-A wiring already populates the
 *     catalog at /api/v1/discovery/scan-catalog).
 *   • See when each engine was last scanned and how many times it ran
 *     in the last 24h (DISC-IMPR-C).
 *   • Auto-deselects engines scanned within the last 6 hours so a
 *     "scan again" click doesn't redundantly re-walk the same targets.
 *
 *   • Phase 2 (#93): Expanding an engine reveals two extra controls:
 *       — "Rules to run" : checkbox list of that engine's enabled
 *          rules, lazy-fetched via shadowDiscoveryRules(engineType).
 *          All-checked by default; deselect to narrow the scan.
 *       — "Targets (override)" : chip-input + chip list. Empty (the
 *          default) means "use the targets each rule already has
 *          configured"; adding any chip overrides every selected rule
 *          for THIS RUN ONLY — the persisted ruleConfig is untouched
 *          (the BE deep-copies + mutates a transient DiscoveryRule).
 *
 *  On confirm, the modal calls back with a Phase 2 payload:
 *      { enginePicks:[{engineType, ruleIds, targets}, …] }
 *  back-compat note: empty enginePicks is blocked at the FE; never
 *  ships "ALL engines" implicitly.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Slideout, Button, Pill } from '../../components/v2';
import { Radar, X as XIcon } from 'lucide-react';
import * as api from '../../services/api';

/* Engine labels come from the scan catalog the backend serves; this file
 * used to keep its own copy of the name for each engine. The copy listed six
 * engines while the platform ran seven, and a name it did not know simply
 * did not appear, so an engine could go missing from this drawer without
 * anything failing. The API is the one source now. */

/* DISC-IMPR-C — Default-deselect engines scanned more recently than
 * this window. Operator can opt back in by re-ticking the checkbox. */
const RECENT_SCAN_WINDOW_MS = 6 * 60 * 60 * 1000; // 6 hours

function fmtAge(ts) {
  if (!ts) return 'never scanned';
  const d = new Date(ts);
  if (isNaN(d.getTime())) return 'never scanned';
  const ageMs = Date.now() - d.getTime();
  if (ageMs < 60_000) return 'just now';
  if (ageMs < 3_600_000) return `${Math.round(ageMs / 60_000)}m ago`;
  if (ageMs < 86_400_000) return `${Math.round(ageMs / 3_600_000)}h ago`;
  return `${Math.round(ageMs / 86_400_000)}d ago`;
}

function isRecentlyScanned(ts) {
  if (!ts) return false;
  const d = new Date(ts);
  if (isNaN(d.getTime())) return false;
  return (Date.now() - d.getTime()) < RECENT_SCAN_WINDOW_MS;
}

export default function ScanTriggerModal({ isOpen, onClose, onConfirm, triggering }) {
  const [catalog, setCatalog] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedEngines, setSelectedEngines] = useState(null); // null until catalog loads
  const [expandedEngine, setExpandedEngine] = useState(null);

  /* Phase 2 state — keyed by engineType.
   *   rulesByEngine          : engineType -> DiscoveryRule[] (loaded on demand)
   *   rulesLoadingFor        : Set<engineType> currently fetching
   *   rulesErrorByEngine     : engineType -> error message
   *   pickedRulesByEngine    : engineType -> Set<ruleId> (defaults to all enabled when first loaded)
   *   pickedTargetsByEngine  : engineType -> string[]    (defaults to [])
   *   targetInputByEngine    : engineType -> in-progress chip input text
   */
  const [rulesByEngine, setRulesByEngine] = useState({});
  const [rulesLoadingFor, setRulesLoadingFor] = useState(new Set());
  const [rulesErrorByEngine, setRulesErrorByEngine] = useState({});
  const [pickedRulesByEngine, setPickedRulesByEngine] = useState({});
  const [pickedTargetsByEngine, setPickedTargetsByEngine] = useState({});
  const [targetInputByEngine, setTargetInputByEngine] = useState({});

  /* Fetch catalog every time the modal opens — the recently-scanned
   * window is time-sensitive, so a stale cache could surface false
   * "already scanned" hints. */
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    /* Reset Phase 2 state on every open so a stale rule-fetch from a
     * previous session can't leak into the new run. */
    setRulesByEngine({});
    setRulesLoadingFor(new Set());
    setRulesErrorByEngine({});
    setPickedRulesByEngine({});
    setPickedTargetsByEngine({});
    setTargetInputByEngine({});
    setExpandedEngine(null);
    api.shadowDiscoveryScanCatalog()
      .then((res) => {
        if (cancelled) return;
        if (res && res.ok === false) {
          setError(res.error || 'Failed to load scan catalog');
          setCatalog(null);
          return;
        }
        const data = res?.data || res;
        const engines = Array.isArray(data?.engines) ? data.engines : [];
        setCatalog(engines);
        /* DISC-IMPR-C — Default selection: every engine NOT scanned
         * recently. Operator can override either way. */
        const defaultSel = new Set();
        engines.forEach((e) => {
          if (!isRecentlyScanned(e.lastScannedAt)) defaultSel.add(e.engineType);
        });
        setSelectedEngines(defaultSel);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e?.message || String(e));
        setCatalog(null);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [isOpen]);

  /* Lazy rule fetch — fires the first time the operator expands an
   * engine card. Cached in rulesByEngine so a collapse + re-expand
   * doesn't re-hit the BE. Default pick = every returned rule (the
   * BE only returns enabled rules; we don't pre-filter further on
   * the FE so the operator sees the full set they have to choose
   * from). */
  const fetchRulesForEngine = useCallback(async (engineType) => {
    if (rulesByEngine[engineType] != null) return;
    if (rulesLoadingFor.has(engineType)) return;
    setRulesLoadingFor((prev) => {
      const next = new Set(prev);
      next.add(engineType);
      return next;
    });
    setRulesErrorByEngine((prev) => {
      const next = { ...prev };
      delete next[engineType];
      return next;
    });
    try {
      const r = await api.shadowDiscoveryRules(engineType);
      const data = r?.data || r;
      const arr = Array.isArray(data) ? data : (Array.isArray(data?.rules) ? data.rules : []);
      /* The list endpoint can return all tenant rules irrespective of
       * the query param if the BE ignores the filter — defensive client
       * narrowing keeps the picker correct either way. */
      const filtered = arr.filter((rule) => !rule.engineType || rule.engineType === engineType);
      setRulesByEngine((prev) => ({ ...prev, [engineType]: filtered }));
      /* Default-check every enabled rule. The BE's pre-flight already
       * enforces "at least 1 enabled rule" before letting the scan
       * proceed, so we know at least one will appear here. */
      setPickedRulesByEngine((prev) => {
        if (prev[engineType] != null) return prev; // operator already chose
        const allIds = new Set(filtered.filter((r) => r.enabled !== false).map((r) => r.id));
        return { ...prev, [engineType]: allIds };
      });
    } catch (e) {
      setRulesErrorByEngine((prev) => ({ ...prev, [engineType]: e?.message || String(e) }));
    } finally {
      setRulesLoadingFor((prev) => {
        const next = new Set(prev);
        next.delete(engineType);
        return next;
      });
    }
  }, [rulesByEngine, rulesLoadingFor]);

  const toggleEngine = (engineType) => {
    setSelectedEngines((prev) => {
      const next = new Set(prev || []);
      if (next.has(engineType)) next.delete(engineType);
      else next.add(engineType);
      return next;
    });
  };
  const selectAll = () => {
    if (!catalog) return;
    setSelectedEngines(new Set(catalog.map((e) => e.engineType)));
  };
  const selectNone = () => setSelectedEngines(new Set());

  const toggleExpanded = (engineType) => {
    setExpandedEngine((prev) => {
      const next = (prev === engineType) ? null : engineType;
      if (next != null) fetchRulesForEngine(next);
      return next;
    });
  };

  const toggleRule = (engineType, ruleId) => {
    setPickedRulesByEngine((prev) => {
      const cur = new Set(prev[engineType] || []);
      if (cur.has(ruleId)) cur.delete(ruleId);
      else cur.add(ruleId);
      return { ...prev, [engineType]: cur };
    });
  };

  const selectAllRules = (engineType) => {
    const rules = rulesByEngine[engineType] || [];
    const allIds = new Set(rules.filter((r) => r.enabled !== false).map((r) => r.id));
    setPickedRulesByEngine((prev) => ({ ...prev, [engineType]: allIds }));
  };
  const selectNoRules = (engineType) => {
    setPickedRulesByEngine((prev) => ({ ...prev, [engineType]: new Set() }));
  };

  const addTarget = (engineType, raw) => {
    const t = (raw || '').trim();
    if (!t) return;
    setPickedTargetsByEngine((prev) => {
      const cur = Array.isArray(prev[engineType]) ? prev[engineType] : [];
      if (cur.includes(t)) return prev; // de-dupe
      return { ...prev, [engineType]: [...cur, t] };
    });
    setTargetInputByEngine((prev) => ({ ...prev, [engineType]: '' }));
  };
  const removeTarget = (engineType, t) => {
    setPickedTargetsByEngine((prev) => {
      const cur = Array.isArray(prev[engineType]) ? prev[engineType] : [];
      return { ...prev, [engineType]: cur.filter((x) => x !== t) };
    });
  };

  const handleTargetKeyDown = (engineType, ev) => {
    /* Enter and comma both commit the chip — Tab is left for a11y. */
    if (ev.key === 'Enter' || ev.key === ',') {
      ev.preventDefault();
      addTarget(engineType, targetInputByEngine[engineType]);
    } else if (ev.key === 'Backspace' && !targetInputByEngine[engineType]) {
      /* Backspace on empty input pops the last chip — common chip-input UX. */
      const cur = pickedTargetsByEngine[engineType] || [];
      if (cur.length > 0) removeTarget(engineType, cur[cur.length - 1]);
    }
  };

  /* Build the Phase 2 payload. For each selected engine:
   *   ruleIds = picked subset, UNLESS the operator hasn't expanded
   *             the card yet (=> ruleIds:[] meaning "all enabled rules"
   *             — the BE applyPickFilter() treats empty as no-filter).
   *   targets = chip list, UNLESS empty (=> targets:[] meaning "use
   *             whatever each rule has configured").
   */
  const buildPicks = () => {
    const sel = Array.from(selectedEngines || []);
    return sel.map((engineType) => {
      const ruleSet = pickedRulesByEngine[engineType];
      const ruleIds = ruleSet ? Array.from(ruleSet) : []; // empty = run all enabled
      const targets = Array.isArray(pickedTargetsByEngine[engineType])
        ? pickedTargetsByEngine[engineType]
        : [];
      return { engineType, ruleIds, targets };
    });
  };

  const handleConfirm = () => {
    const picks = buildPicks();
    if (picks.length === 0) return;
    /* Guard rail: every selected engine the operator EXPANDED must
     * have at least one rule checked. If they expanded and unchecked
     * everything, blocking the run prevents a confusing zero-result
     * scan. Engines they never expanded keep ruleIds:[] (= run-all). */
    for (const pick of picks) {
      const expanded = rulesByEngine[pick.engineType] != null;
      const ruleSet = pickedRulesByEngine[pick.engineType];
      if (expanded && ruleSet && ruleSet.size === 0) return;
    }
    onConfirm({ enginePicks: picks });
  };

  const selectedCount = selectedEngines ? selectedEngines.size : 0;
  const totalCount = catalog ? catalog.length : 0;

  /* Footer summary — count picked rules + override targets across the
   * selected engines so the operator gets an at-a-glance feel for what
   * they're about to dispatch. Unexpanded engines contribute "all
   * enabled rules" (rendered as "rules") since we don't know the
   * exact count without a network round-trip. */
  const ruleCountSummary = (() => {
    if (!selectedEngines) return 0;
    let total = 0;
    let allExplicit = true;
    selectedEngines.forEach((engineType) => {
      const ruleSet = pickedRulesByEngine[engineType];
      if (ruleSet) total += ruleSet.size;
      else allExplicit = false;
    });
    return { total, allExplicit };
  })();
  const overrideTargetTotal = (() => {
    if (!selectedEngines) return 0;
    let total = 0;
    selectedEngines.forEach((engineType) => {
      const arr = pickedTargetsByEngine[engineType];
      if (Array.isArray(arr)) total += arr.length;
    });
    return total;
  })();

  let confirmLabel;
  if (triggering) {
    confirmLabel = 'Starting scan…';
  } else if (selectedCount === 0) {
    confirmLabel = 'Select at least one engine';
  } else {
    const ruleSeg = ruleCountSummary.allExplicit && ruleCountSummary.total > 0
      ? `${ruleCountSummary.total} rule${ruleCountSummary.total === 1 ? '' : 's'}`
      : 'rules';
    const tgtSeg = overrideTargetTotal > 0
      ? ` · ${overrideTargetTotal} override target${overrideTargetTotal === 1 ? '' : 's'}`
      : '';
    confirmLabel = `Run ${selectedCount} engine${selectedCount === 1 ? '' : 's'} · ${ruleSeg}${tgtSeg}`;
  }

  return (
    <Slideout
      isOpen={isOpen}
      onClose={triggering ? null : onClose}
      dismissOnBackdrop={!triggering}
      width={720}
      ariaLabel="Run discovery scan"
      header={
        <div className="flex items-center gap-2 px-4 py-3 border-b border-v2-border-subtle">
          <Radar size={16} aria-hidden="true" className="text-v2-action" />
          <div className="flex-1 min-w-0">
            <div className="text-[13px] font-semibold text-v2-ink leading-none">Run discovery scan</div>
            <div className="text-[10.5px] text-v2-ink-muted mt-0.5">
              Pick engines · narrow rules · override targets (this run only)
            </div>
          </div>
        </div>
      }
      footer={
        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-v2-border-subtle bg-v2-page">
          <Button variant="ghost" onClick={onClose} disabled={triggering}>Cancel</Button>
          <Button
            variant="primary"
            onClick={handleConfirm}
            disabled={loading || triggering || selectedCount === 0}
            loading={triggering}
          >
            {confirmLabel}
          </Button>
        </div>
      }
    >
      <div className="space-y-3 text-[12.5px] px-4 py-3">
        <p className="text-v2-ink-secondary leading-relaxed">
          Pick which engines should run. Engines scanned in the last <strong>6 hours</strong>
          {' '}are deselected by default. Expand an engine to narrow which rules execute
          and to override targets for this run only — overrides are <em>transient</em> and
          never persist to the rule definition.
        </p>

        {loading && (
          <div className="text-v2-ink-muted italic py-4 text-center">Loading scan catalog…</div>
        )}

        {error && !loading && (
          <div className="rounded-v2-sm border border-v2-danger bg-v2-danger-soft text-v2-danger px-3 py-2">
            <strong>Failed to load catalog:</strong> {error}
          </div>
        )}

        {!loading && !error && catalog && (
          <>
            <div className="flex items-center justify-between text-[11px] text-v2-ink-muted">
              <span>{selectedCount} of {totalCount} engines selected</span>
              <div className="flex gap-2">
                <button
                  type="button"
                  className="text-v2-action hover:underline"
                  onClick={selectAll}
                  disabled={triggering}
                >
                  Select all
                </button>
                <span>·</span>
                <button
                  type="button"
                  className="text-v2-action hover:underline"
                  onClick={selectNone}
                  disabled={triggering}
                >
                  Deselect all
                </button>
              </div>
            </div>

            <ul className="space-y-2">
              {catalog.map((e) => {
                const checked = !!(selectedEngines && selectedEngines.has(e.engineType));
                const recent = isRecentlyScanned(e.lastScannedAt);
                const cfgTargets = Array.isArray(e.targets) ? e.targets : [];
                const isExpanded = expandedEngine === e.engineType;
                const rulesLoaded = rulesByEngine[e.engineType];
                const rulesLoading = rulesLoadingFor.has(e.engineType);
                const rulesError = rulesErrorByEngine[e.engineType];
                const pickedRules = pickedRulesByEngine[e.engineType];
                const pickedTargets = pickedTargetsByEngine[e.engineType] || [];
                const targetInput = targetInputByEngine[e.engineType] || '';
                const rulePickCount = pickedRules ? pickedRules.size : null;
                const ruleTotal = rulesLoaded ? rulesLoaded.filter((r) => r.enabled !== false).length : null;

                return (
                  <li
                    key={e.engineType}
                    className={
                      'border rounded-v2-md transition-colors ' +
                      (checked ? 'border-v2-action bg-v2-action-soft' : 'border-v2-border-subtle')
                    }
                  >
                    <label className="flex items-start gap-3 p-3 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        className="v2-focus-ring mt-0.5"
                        checked={checked}
                        onChange={() => toggleEngine(e.engineType)}
                        disabled={triggering}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-v2-ink">{e.label || e.engineType}</span>
                          <span className="text-[10px] text-v2-ink-muted font-mono-v2">{e.engineType}</span>
                          {recent && (
                            <Pill tone="muted" size="sm">recently scanned</Pill>
                          )}
                          {!recent && e.lastScannedAt && (
                            <Pill tone="cyan" size="sm">{fmtAge(e.lastScannedAt)}</Pill>
                          )}
                          {!e.lastScannedAt && (
                            <Pill tone="warning" size="sm">never scanned</Pill>
                          )}
                          {checked && rulePickCount != null && ruleTotal != null && (
                            <Pill tone={rulePickCount === 0 ? 'danger' : 'cyan'} size="sm">
                              {rulePickCount} of {ruleTotal} rules
                            </Pill>
                          )}
                          {checked && pickedTargets.length > 0 && (
                            <Pill tone="warning" size="sm">
                              {pickedTargets.length} target override{pickedTargets.length === 1 ? '' : 's'}
                            </Pill>
                          )}
                        </div>
                        <div className="text-[11px] text-v2-ink-muted mt-0.5">
                          {cfgTargets.length === 0
                            ? 'No configured targets — rule-driven detection.'
                            : `${cfgTargets.length} configured target${cfgTargets.length === 1 ? '' : 's'} on file`}
                          {typeof e.runsLast24h === 'number' && e.runsLast24h > 0 && (
                            <> · {e.runsLast24h} run{e.runsLast24h === 1 ? '' : 's'} in last 24h</>
                          )}
                          {typeof e.lastRunAgents === 'number' && e.lastRunAgents > 0 && (
                            <> · last run found {e.lastRunAgents} agent{e.lastRunAgents === 1 ? '' : 's'}</>
                          )}
                        </div>
                      </div>
                      <button
                        type="button"
                        className="text-[11px] text-v2-action hover:underline whitespace-nowrap"
                        onClick={(ev) => {
                          ev.preventDefault();
                          toggleExpanded(e.engineType);
                        }}
                      >
                        {isExpanded ? 'Hide options' : 'Configure'}
                      </button>
                    </label>

                    {isExpanded && (
                      <div className="px-3 pb-3 -mt-1 space-y-3 border-t border-v2-border-subtle pt-3">
                        {/* RULES SECTION */}
                        <section>
                          <div className="flex items-center justify-between mb-1">
                            <div className="text-[10px] uppercase tracking-wider text-v2-ink-muted">
                              Rules to run
                            </div>
                            {rulesLoaded && rulesLoaded.length > 0 && (
                              <div className="flex gap-2 text-[10.5px]">
                                <button
                                  type="button"
                                  className="text-v2-action hover:underline"
                                  onClick={() => selectAllRules(e.engineType)}
                                  disabled={triggering}
                                >
                                  All
                                </button>
                                <span className="text-v2-ink-muted">·</span>
                                <button
                                  type="button"
                                  className="text-v2-action hover:underline"
                                  onClick={() => selectNoRules(e.engineType)}
                                  disabled={triggering}
                                >
                                  None
                                </button>
                              </div>
                            )}
                          </div>
                          {rulesLoading && (
                            <div className="text-v2-ink-muted italic text-[11px] py-2">Loading rules…</div>
                          )}
                          {rulesError && !rulesLoading && (
                            <div className="rounded-v2-sm border border-v2-danger bg-v2-danger-soft text-v2-danger text-[11px] px-2 py-1.5">
                              Failed to load rules: {rulesError}
                            </div>
                          )}
                          {!rulesLoading && !rulesError && rulesLoaded && rulesLoaded.length === 0 && (
                            <div className="text-[11px] text-v2-ink-muted italic">
                              No rules configured for this engine. The scan will no-op for this engine.
                            </div>
                          )}
                          {!rulesLoading && !rulesError && rulesLoaded && rulesLoaded.length > 0 && (
                            <ul className="space-y-1 max-h-44 overflow-y-auto pr-1">
                              {rulesLoaded.map((rule) => {
                                const ruleChecked = !!(pickedRules && pickedRules.has(rule.id));
                                const disabled = rule.enabled === false;
                                return (
                                  <li key={rule.id}>
                                    <label
                                      className={
                                        'flex items-start gap-2 px-2 py-1.5 rounded-v2-sm border text-[11.5px] ' +
                                        (ruleChecked
                                          ? 'border-v2-action bg-v2-action-soft'
                                          : 'border-v2-border-subtle bg-v2-surface')
                                      }
                                    >
                                      <input
                                        type="checkbox"
                                        className="v2-focus-ring mt-0.5"
                                        checked={ruleChecked}
                                        disabled={triggering || disabled}
                                        onChange={() => toggleRule(e.engineType, rule.id)}
                                      />
                                      <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-1.5 flex-wrap">
                                          <span className="font-mono-v2 text-v2-ink">{rule.ruleName}</span>
                                          {rule.severity && (
                                            <Pill
                                              tone={rule.severity === 'CRITICAL' || rule.severity === 'HIGH' ? 'danger' : 'muted'}
                                              size="sm"
                                            >
                                              {rule.severity}
                                            </Pill>
                                          )}
                                          {disabled && <Pill tone="muted" size="sm">disabled</Pill>}
                                        </div>
                                        {rule.description && (
                                          <div className="text-[10.5px] text-v2-ink-muted mt-0.5 truncate" title={rule.description}>
                                            {rule.description}
                                          </div>
                                        )}
                                      </div>
                                    </label>
                                  </li>
                                );
                              })}
                            </ul>
                          )}
                        </section>

                        {/* TARGETS OVERRIDE SECTION */}
                        <section>
                          <div className="text-[10px] uppercase tracking-wider text-v2-ink-muted mb-1">
                            Targets (override · this run only)
                          </div>
                          <div className="text-[10.5px] text-v2-ink-muted mb-1.5 leading-snug">
                            Leave empty to use the targets each rule has configured. Add domains,
                            IPs, repos, or whatever the rule consumes — entries here override
                            <code className="font-mono-v2 px-0.5">ruleConfig.targets</code> for
                            every checked rule on this run and are not persisted.
                          </div>
                          {pickedTargets.length > 0 && (
                            <ul className="flex flex-wrap gap-1 mb-1.5">
                              {pickedTargets.map((t) => (
                                <li
                                  key={t}
                                  className="inline-flex items-center gap-1 rounded-v2-sm border border-v2-border-subtle bg-v2-surface px-1.5 py-0.5 text-[11px] font-mono-v2 text-v2-ink"
                                >
                                  <span className="truncate max-w-[18rem]" title={t}>{t}</span>
                                  <button
                                    type="button"
                                    className="text-v2-ink-muted hover:text-v2-danger"
                                    onClick={() => removeTarget(e.engineType, t)}
                                    disabled={triggering}
                                    aria-label={`Remove target ${t}`}
                                  >
                                    <XIcon size={12} />
                                  </button>
                                </li>
                              ))}
                            </ul>
                          )}
                          <div className="flex items-center gap-2">
                            <input
                              type="text"
                              className="flex-1 px-2 py-1 text-[11.5px] rounded-v2-sm border border-v2-border-subtle bg-v2-surface text-v2-ink font-mono-v2 v2-focus-ring"
                              placeholder="api.example.com   (press Enter or , to add)"
                              value={targetInput}
                              disabled={triggering}
                              onChange={(ev) => setTargetInputByEngine((prev) => ({ ...prev, [e.engineType]: ev.target.value }))}
                              onKeyDown={(ev) => handleTargetKeyDown(e.engineType, ev)}
                            />
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => addTarget(e.engineType, targetInput)}
                              disabled={triggering || !targetInput.trim()}
                            >
                              Add
                            </Button>
                          </div>
                          {cfgTargets.length > 0 && (
                            <details className="mt-2">
                              <summary className="text-[10.5px] text-v2-ink-muted cursor-pointer hover:text-v2-action">
                                View {cfgTargets.length} configured target{cfgTargets.length === 1 ? '' : 's'} (rule defaults)
                              </summary>
                              <ul className="mt-1 space-y-0.5 max-h-32 overflow-y-auto pl-2">
                                {cfgTargets.map((t, i) => (
                                  <li key={i} className="text-[10.5px] text-v2-ink-secondary font-mono-v2 truncate" title={t}>
                                    · {t}
                                  </li>
                                ))}
                              </ul>
                            </details>
                          )}
                        </section>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </div>
    </Slideout>
  );
}
